import { useEffect, useState } from "react";
import { Mail, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import api, { errorMessage } from "@/services/api";

/**
 * Full-screen blocking gate shown to authenticated-but-unverified sellers.
 *
 * There is no cancel / close path — the only exits are:
 *   1. Verify via the email link (opens the public /verify-email page), then
 *      return here and click "I've verified my email" (or just refocus the
 *      window — a `focus` listener re-checks /auth/me automatically).
 *   2. Log out.
 *
 * The seller keeps the current user in AuthContext React state (NOT
 * localStorage), so we clear the gate by calling `refreshUser()`, which
 * re-reads /auth/me into state. Once is_verified is true, ProtectedRoute
 * re-renders and drops this gate automatically.
 */
export default function VerifyEmailScreen({ user }) {
  const { refreshUser, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);

  const refreshVerification = async () => {
    try {
      const data = await refreshUser();
      return !!(data && data.is_verified === true);
    } catch {
      // Best-effort — swallow and let the user retry.
      return false;
    }
  };

  // Auto re-check whenever the seller returns to this tab (e.g. after clicking
  // the verification link in another tab and coming back).
  useEffect(() => {
    const onFocus = () => refreshVerification();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post("/auth/resend-verification");
      toast.success(res.data?.message || "Verification email sent.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setResending(false);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    const verified = await refreshVerification();
    if (verified) {
      toast.success("Your email is verified. Welcome!");
    } else {
      toast.error("We haven't received your verification yet. Please click the link in your email.");
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
            <Mail className="h-7 w-7 text-darkblue" />
          </div>
          <CardTitle>Verify your email</CardTitle>
          <CardDescription>
            We've sent a verification link to
            {user?.email ? (
              <span className="block font-semibold text-gray-800 mt-1">{user.email}</span>
            ) : (
              " your email address"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            Click the link in that email to activate your account. You'll need to
            verify before you can review quotations.
          </p>

          <Button
            onClick={handleCheck}
            disabled={checking}
            className="w-full h-11 bg-darkblue hover:bg-darkblue-hover text-white font-semibold"
          >
            {checking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Checking...
              </>
            ) : (
              "I've verified my email"
            )}
          </Button>

          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resending}
            className="w-full h-11 border-brand-500 text-brand-600 hover:bg-brand-50"
          >
            {resending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Resend verification email
              </>
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={logout}
              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
            >
              Log out
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
